import React, { useEffect } from 'react';
import '../css/ProductSelection.scss';
import { useFormikContext } from 'formik';
import { AvailableProducts, MagicianSubProducts } from 'app/constants/Products';
import protocolNames from 'app/utils/protocolNames';
import subProductNames from 'app/utils/subProductNames';
import { Badge, Card, Button } from 'mui';
import Radio from 'app/components/common/Radio';
import PriceFormatter from 'app/components/customer/steps/Payment/Generic/PriceFormatter';
import { MAGICIAN_SUBSCRIPTION_MONTHLY_COST } from 'app/helpers/productSpecificPriceCalculators/magicianPriceCalculator';
import assetLibrary from 'app/utils/assetLibrary';
import { drugVariantIsMicro, getDrugVariantForDoseType, getStrengthVariantForDrugAndDose } from './magicianUtils';

type Props = {
  firstTimeChoice?: boolean;
  handleSubmit?: () => void;
  changeProductHandler?: ((kingProductType: string, setFieldValue: any) => void) | null;
  multimonth_plan?: number;
  starter_pack: boolean;
  available_doses: string[];
};

const MagicianDrugSelectionForm = ({
  firstTimeChoice = true,
  changeProductHandler = null,
  handleSubmit = undefined,
  starter_pack = false,
}: Props) => {
  const { values, setFieldValue } = useFormikContext<{
    drug_variant: string;
    strength_variant: string;
    dose_type: string;
  }>();

  useEffect(() => {
    if (values.dose_type === 'micro') {
      setFieldValue('drug_variant', 'semaglutide_micro');
    } else {
      handleChange(values.drug_variant);
    }
  }, []);

  const handleChange = (value: string) => {
    setFieldValue('drug_variant', getDrugVariantForDoseType(value, values.dose_type));
    setFieldValue('strength_variant', getStrengthVariantForDrugAndDose(value, values.dose_type));

    if (drugVariantIsMicro(value)) {
      setFieldValue('multimonth_plan', null);
    }

    if (changeProductHandler) {
      changeProductHandler(value, setFieldValue);
    }
  };

  const subProductConfigPrice = (product) => {
    if (values.dose_type === 'micro') {
      return MAGICIAN_SUBSCRIPTION_MONTHLY_COST[product]['micro'];
    } else if (starter_pack || firstTimeChoice) {
      return MAGICIAN_SUBSCRIPTION_MONTHLY_COST[product]['starter_pack'] / 3;
    } else {
      return MAGICIAN_SUBSCRIPTION_MONTHLY_COST[product]['standard'];
    }
  };

  const subProductsConfig = [
    {
      name: MagicianSubProducts.Semaglutide,
      price: subProductConfigPrice(MagicianSubProducts.Semaglutide),
      description:
        values.dose_type === 'micro'
          ? 'A once-weekly injection targeting GLP-1 receptors to provide gradual weight management with powerful metabolic benefits—including reduced inflammation, better heart health, and brain protection.'
          : 'Unlock a slimmer you with GLP-1 - proven to help shed an average of 16.9% body weight in 68 weeks.',
    },
    {
      name: MagicianSubProducts.Tirzepatide,
      price: subProductConfigPrice(MagicianSubProducts.Tirzepatide),
      description:
        values.dose_type === 'micro'
          ? 'A once-weekly injection that offers the benefits of GLP-1 plus superior weight loss, glucose control, and insulin sensitivity by acting on the GIP receptor.'
          : 'Discover the most effective weight loss tool on the market. People using GLP-1 + GIP lose more weight than any other GLP-1 product. GLP-1 + GIP patients lose on average 22.5% of body weight in 72 weeks.',
    },
  ];

  const isRadioChecked = (subProduct) => {
    if (values.dose_type === 'micro') {
      return `${subProduct.name}_micro` === values.drug_variant;
    }
    return subProduct.name === values.drug_variant.replace('_micro', '');
  };

  const isTirzepatideDisabled = (subProduct) =>
    subProduct.notAvailableInState ||
    (subProduct.name === MagicianSubProducts.Tirzepatide && values.dose_type === 'micro');

  const renderRadio = (subProduct, showDivider) => (
    <>
      <Radio
        className="no-borders top-aligned"
        onChange={() => handleChange(subProduct.name)}
        checked={isRadioChecked(subProduct)}
        disabled={isTirzepatideDisabled(subProduct)}
        testId={`drug-variant-${subProduct.name}`}
        label={
          <div className="-mt-6 lg:-mt-5">
            <div className="flex flex-row justify-between items-end">
              <h5>{subProductNames[AvailableProducts.Magician][subProduct.name]}</h5>
              <PriceFormatter period="mo" from price={subProduct.price} />
            </div>
            <p className="mt-2 lg:mt-3 text-sm lg:text-base">{subProduct.description}</p>
          </div>
        }
      />
      {isTirzepatideDisabled(subProduct) && <Badge variant="outline">Not Available</Badge>}
      {showDivider && <hr />}
    </>
  );

  const image = assetLibrary[`prod-magician-${starter_pack ? 'starter' : values.drug_variant}`];

  return (
    <Card>
      <Card.Body>
        <figure className="h-[180px] lg:h-[240px] mb-4">
          <img className="h-full w-auto" src={image} alt="Product" />
        </figure>

        <div className="text-center mt-4">
          <Badge variant={AvailableProducts.Magician} className="mb-2">
            {protocolNames[AvailableProducts.Magician]}
          </Badge>
          {values.dose_type === 'micro' && <h4>Microdose</h4>}
        </div>

        {firstTimeChoice && starter_pack && values.dose_type === 'standard' && (
          <div className="mb-3">
            <h5 className="mb-2">Starter Pack</h5>
            <p className="text-sm mb-6">
              Choose either GLP-1 Protocol or GLP-1 + GIP starter pack for 3 months of medication for one low price.
            </p>
            <hr />
          </div>
        )}

        {subProductsConfig.map((subProduct, index) => renderRadio(subProduct, index !== subProductsConfig.length - 1))}

        {values.dose_type === 'micro' && (
          <>
            <hr />
            <p className="text-sm mb-6">
              Prices shown are per month, but billing and shipping occur every 10 weeks and include a 10-week supply.
            </p>
          </>
        )}
        {handleSubmit && (
          <>
            {values.dose_type === 'standard' && <hr />}

            <Button onClick={handleSubmit} data-testid="submit-product-selection">
              Continue
            </Button>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default MagicianDrugSelectionForm;
